import { formatRepoFiltersToApiFilters } from '@webapp/core/utils/repositories/formatRepoFiltersToApiFilters'
import { throwRepoErrorFromApiV3Error } from '@webapp/core/utils/repositories/throwRepoErrorFromApiV3Error'
import { type RepoFilterParams } from '@webapp/infrastructure/interfaces/repositories/RepoFilterParams'
import { type RepoPaginatedResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginatedResponse'
import { type RepoStandardResponse } from '@webapp/infrastructure/interfaces/repositories/RepoStandardResponse'
import { type IHttpClient } from '@webapp/infrastructure/interfaces/services/IHttpClient'
import { type IPaymentMethodRepository } from '@webapp/platform/banking/business-logic/interfaces/IPaymentMethodRepository'
import { type PaymentMethodConstructor } from '@webapp/platform/banking/entities/PaymentMethod'
import { type PaymentMethodApiModel } from '../../api-models/PaymentMethodApiModel'
import { mapPaymentMethodApiModel } from '../../mappers/mapPaymentMethodApiModel'
import {
  type CreateAirwallexPaymentMethodPayload,
  type CreateExternalPaymentMethodPayload,
} from '../../types/CreatePaymentMethodPayload'
import { type GetPaymentMethodsResponse } from '../../types/GetPaymentMethodsResponse'

interface Dependency {
  httpClient: IHttpClient
}

export class PaymentMethodRepository implements IPaymentMethodRepository {
  constructor(private readonly deps: Dependency) {}

  async getPaymentMethods(
    params?: RepoFilterParams
  ): Promise<RepoPaginatedResponse<PaymentMethodConstructor>> {
    const fieldMapping = {
      currencyId: 'currency',
      vendorId: 'vendor',
    } as const

    const formattedQueryParams: RepoFilterParams = params || {}

    if (params) {
      Object.entries(fieldMapping).forEach(([inputKey, outputKey]) => {
        if (params[inputKey] !== undefined) {
          formattedQueryParams[outputKey] = params[inputKey]
          formattedQueryParams[inputKey] = undefined
        }
      })
    }

    const res = await this.deps.httpClient.get<GetPaymentMethodsResponse>(
      '/api/v2/ap/vendor-payment-methods/',
      {
        params: {
          ...formatRepoFiltersToApiFilters(formattedQueryParams),
        },
      }
    )

    const data = res.data.data
    const metadata = res.data.metadata

    return {
      data: data.map((d) => mapPaymentMethodApiModel(d)),
      pagination: {
        count: metadata?.pagination?.count || 0,
        page: metadata?.pagination?.current_page || 0,
        pageSize: metadata?.pagination?.page_size || 0,
        numPages: metadata?.pagination?.num_pages || 0,
      },
    }
  }

  async createExternalPaymentMethod(
    payload: CreateExternalPaymentMethodPayload
  ): Promise<RepoStandardResponse<PaymentMethodConstructor>> {
    try {
      const res = await this.deps.httpClient.post<
        RepoStandardResponse<PaymentMethodApiModel>,
        CreateExternalPaymentMethodPayload
      >('/api/v2/ap/vendor-payment-methods/', payload)

      const responseData = res.data.data

      return { data: mapPaymentMethodApiModel(responseData) }
    } catch (error) {
      throw throwRepoErrorFromApiV3Error(error)
    }
  }

  async createAirwallexPaymentMethod(
    payload: CreateAirwallexPaymentMethodPayload
  ): Promise<RepoStandardResponse<PaymentMethodConstructor>> {
    try {
      const res = await this.deps.httpClient.post<
        RepoStandardResponse<PaymentMethodApiModel>,
        CreateAirwallexPaymentMethodPayload
      >('/api/v3/payments/vendor-payment-methods/', payload)

      const responseData = res.data.data

      return { data: mapPaymentMethodApiModel(responseData) }
    } catch (error) {
      throw throwRepoErrorFromApiV3Error(error)
    }
  }
}
